import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Save, Building2, Calendar, MapPin, FileText } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

const AdminWorkOrderForm = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData;
  const isEditMode = !!editData;
  const initializedWorkOrderId = useRef(null);

  // Trade types as per specification
  const tradeTypes = [
    'Janitorial',
    'Event Support',
    'Landscape',
    'Security',
    'Sweeping',
    'General Maintenance',
    'Special Projects'
  ];

  // Priority levels as per specification
  const priorities = [
    { value: 'P1', label: 'P1: Within 2 hours' },
    { value: 'P2', label: 'P2: Same evening' },
    { value: 'P3', label: 'P3: Same week' },
    { value: 'P4', label: 'P4: Within 30 days' }
  ];

  // Customer and location dropdowns
  const [customers, setCustomers] = useState([]);
  const [serviceLocations, setServiceLocations] = useState([]);
  const [fieldUsers, setFieldUsers] = useState([]);
  const [useOneOffCustomer, setUseOneOffCustomer] = useState(false);
  const [useOneOffLocation, setUseOneOffLocation] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    // Section 1: Job Information
    job_name: '',
    trade_type: '',
    description_of_work: '',
    work_order_received_by: '',
    customer_work_order_number: '',
    customer_po_number: '',
    customer_id: '',
    customer_name: '',
    main_contact_name: '',
    main_contact_email: '',
    main_contact_phone: '',
    customer_email: '',
    customer_phone: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    
    // Section 2: Location Information
    location_id: '',
    service_location: '',
    service_address: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    local_rep_name: '',
    local_rep_email: '',
    local_rep_phone: '',
    
    // Section 3: Job Timing
    date_received: new Date().toISOString().split('T')[0],
    required_service_date: '',
    priority_level: '',
    
    // Section 4: Additional Instructions
    local_contact_name: '',
    local_contact_phone: '',
    special_instructions: '',
    billable: true,
    
    // Field User Assignment (CRITICAL)
    assigned_to: ''
  });

  // Load customers and field users on mount
  useEffect(() => {
    console.log('🔄 [DATA LOAD useEffect] Running to load customers and field users');
    const loadData = async () => {
      await fetchCustomers();
      await fetchFieldUsers();
      console.log('✅ [DATA LOAD useEffect] Customers and field users loaded');
    };
    
    loadData();
  }, [token]); // Re-fetch if token changes

  // Initialize edit mode - SINGLE useEffect to avoid race conditions
  useEffect(() => {
    console.log('🔄 [EDIT INIT useEffect] Running. isEditMode:', isEditMode, 'editData.id:', editData?.id, 'initializedWorkOrderId:', initializedWorkOrderId.current);
    
    // Only initialize if we haven't already initialized this specific work order
    if (isEditMode && editData && editData.id && initializedWorkOrderId.current !== editData.id) {
      initializedWorkOrderId.current = editData.id;
      console.log('🚀 [EDIT INIT] Initializing form for work order:', editData.id);
      console.log('🔍 DEBUG FULL EDIT DATA LOADED:', {
        workOrderId: editData.id,
        priority_level: editData.priority_level,
        assigned_to: editData.assigned_to,
        customer_id: editData.customer_id,
        customer_name: editData.customer_name,
        trade_type: editData.trade_type,
        location_id: editData.location_id,
        service_location: editData.service_location,
        contact_person: editData.contact_person,
        contact_phone: editData.contact_phone
      });
      
      console.log('✅ [EDIT MODE INIT] Creating newFormData with specific values:');
      console.log('  📋 trade_type from editData:', editData.trade_type);
      console.log('  📋 priority_level from editData:', editData.priority_level);
      console.log('  📋 customer_id from editData:', editData.customer_id);
      console.log('  📋 location_id from editData:', editData.location_id);
      console.log('  📋 service_location from editData:', editData.service_location);
      
      // Set one-off flags IMMEDIATELY to ensure correct UI rendering
      setUseOneOffCustomer(!editData.customer_id);
      setUseOneOffLocation(!editData.location_id);
      console.log('🔄 [EDIT INIT] Set one-off flags:', {
        useOneOffCustomer: !editData.customer_id,
        useOneOffLocation: !editData.location_id,
        has_customer_id: !!editData.customer_id,
        has_location_id: !!editData.location_id
      });
      
      // Load locations if customer exists - do this BEFORE setTimeout to ensure data is ready
      if (editData.customer_id) {
        console.log('🔄 [EDIT INIT] Fetching locations for customer_id:', editData.customer_id);
        fetchLocationsByCustomer(editData.customer_id);
        console.log('✅ [EDIT INIT] Location fetch initiated');
      }
    
      // Use setTimeout to ensure state updates don't conflict with each other
      // AND to ensure serviceLocations are loaded before setting formData
      setTimeout(() => {
        const newFormData = {
          job_name: editData.job_name || '',
          trade_type: editData.trade_type || '',
          description_of_work: editData.description_of_work || '',
          work_order_received_by: editData.work_order_received_by || '',
          customer_work_order_number: editData.customer_work_order_number || '',
          customer_po_number: editData.customer_po_number || '',
          customer_id: editData.customer_id || '',
          customer_name: editData.customer_name || '',
          main_contact_name: editData.main_contact_name || '',
          main_contact_email: editData.main_contact_email || '',
          main_contact_phone: editData.main_contact_phone || '',
          customer_email: editData.customer_email || '',
          customer_phone: editData.customer_phone || '',
          contact_person: editData.contact_person || '',
          contact_phone: editData.contact_phone || '',
          contact_email: editData.contact_email || '',
          location_id: editData.location_id || '',
          service_location: editData.service_location || '',
          service_address: editData.service_address || '',
          service_city: editData.service_city || '',
          service_state: editData.service_state || '',
          service_zip: editData.service_zip || '',
          date_received: editData.date_received || new Date().toISOString().split('T')[0],
          required_service_date: editData.required_service_date || '',
          priority_level: editData.priority_level || '',
          local_rep_name: editData.local_rep_name || '',
          local_rep_email: editData.local_rep_email || '',
          local_rep_phone: editData.local_rep_phone || '',
          local_contact_name: editData.local_contact_name || '',
          local_contact_phone: editData.local_contact_phone || '',
          special_instructions: editData.special_instructions || '',
          billable: editData.billable !== undefined ? editData.billable : true,
          assigned_to: editData.assigned_to || ''
        };
        
        console.log('✅ [EDIT MODE INIT] newFormData created with values:');
        console.log('  📋 newFormData.trade_type:', newFormData.trade_type);
        console.log('  📋 newFormData.priority_level:', newFormData.priority_level);
        console.log('  📋 newFormData.customer_id:', newFormData.customer_id);
        console.log('  📋 newFormData.location_id:', newFormData.location_id);
        console.log('  📋 newFormData.service_location:', newFormData.service_location);
        
        console.log('✅ [EDIT MODE INIT] Setting FormData with:', {
          priority_level: newFormData.priority_level,
          assigned_to: newFormData.assigned_to,
          customer_id: newFormData.customer_id,
          trade_type: newFormData.trade_type,
          location_id: newFormData.location_id,
          service_location: newFormData.service_location
        });
        
        setFormData(newFormData);
        console.log('✅ [EDIT MODE INIT] setFormData called');
        
        // Additional validation: Check if location exists in serviceLocations
        setTimeout(() => {
          console.log('🔍 [LOCATION VALIDATION] serviceLocations.length:', serviceLocations.length);
          if (editData.location_id && serviceLocations.length > 0) {
            const locationExists = serviceLocations.find(loc => loc.id === editData.location_id);
            console.log('🔍 [LOCATION VALIDATION] Location exists in array:', !!locationExists);
            if (locationExists) {
              console.log('✅ [LOCATION VALIDATION] Location found:', locationExists.location_name);
            } else {
              console.log('❌ [LOCATION VALIDATION] Location NOT found in serviceLocations array');
              console.log('🔍 [LOCATION VALIDATION] Available location IDs:', serviceLocations.map(l => l.id).slice(0, 5));
            }
          }
        }, 200);
      }, 150); // Increased delay to allow service locations to load
    } else if (!isEditMode) {
      // Reset the ref when switching to create mode
      initializedWorkOrderId.current = null;
      console.log('🔄 [CREATE MODE] Resetting initialization tracker');
    }
  }, [isEditMode, editData]);

  // Debug: Track critical field changes with delayed logging to avoid interference
  useEffect(() => {
    if (isEditMode) {
      // Debounce the logging to avoid excessive renders
      const timeoutId = setTimeout(() => {
        console.log('📊 formData state check:', {
          priority_level: formData.priority_level || '(EMPTY)',
          customer_id: formData.customer_id || '(EMPTY)',
          trade_type: formData.trade_type || '(EMPTY)',
          assigned_to: formData.assigned_to ? formData.assigned_to.substring(0, 8) + '...' : '(EMPTY)'
        });
      }, 200); // Longer delay to let state settle
      
      return () => clearTimeout(timeoutId);
    }
  }, [formData, isEditMode]); // Only depend on the entire formData object

  const fetchCustomers = async () => {
    try {
      console.log('Fetching customers from:', `${API}/customers/names`);
      const response = await axios.get(`${API}/customers/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Customers fetched:', response.data?.length || 0, 'customers');
      setCustomers(response.data || []);
      if (response.data?.length === 0) {
        toast.info('No customers found in database');
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
      setCustomers([]);
    }
  };

  const fetchFieldUsers = async () => {
    try {
      console.log('Fetching field users from:', `${API}/work-orders/field-users`);
      const response = await axios.get(`${API}/work-orders/field-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Field users fetched:', response.data?.length || 0, 'users');
      console.log('Field user IDs:', response.data?.map(u => u.id) || []);
      setFieldUsers(response.data || []);
      if (response.data?.length === 0) {
        toast.warning('No field users found. Please create field user accounts first.');
      }
    } catch (error) {
      console.error('Failed to fetch field users:', error);
      toast.error('Failed to load field users');
      setFieldUsers([]);
    }
  };

  const fetchLocationsByCustomer = async (customerId) => {
    if (!customerId) {
      setServiceLocations([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/service-locations/by-customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      setServiceLocations([]);
    }
  };

  const handleCustomerChange = (customerId) => {
    console.log('🔄 [handleCustomerChange] Called with customerId:', customerId, 'isEditMode:', isEditMode);
    if (!customerId) {
      console.log('🔄 [handleCustomerChange] Clearing customer fields because customerId is empty');
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: '',
        main_contact_name: '',
        main_contact_email: '',
        main_contact_phone: '',
        location_id: '',
        service_location: ''
      }));
      setServiceLocations([]);
      return;
    }
    
    const selectedCustomer = customers.find(c => c.id === customerId);
    if (selectedCustomer) {
      console.log('✅ [handleCustomerChange] Customer Master Data Auto-Populated:', {
        name: selectedCustomer.name,
        main_contact_name: selectedCustomer.main_contact_name,
        main_contact_phone: selectedCustomer.main_contact_phone
      });
      
      setFormData(prev => ({
        ...prev,
        customer_id: customerId,
        customer_name: selectedCustomer.name || selectedCustomer.company_name || '',
        // Auto-populate work order contact fields from customer master
        contact_person: selectedCustomer.main_contact_name || '',
        contact_phone: selectedCustomer.main_contact_phone || '',
        contact_email: selectedCustomer.main_contact_email || '',
        // Keep reference to customer master data
        main_contact_name: selectedCustomer.main_contact_name || '',
        main_contact_email: selectedCustomer.main_contact_email || '',
        main_contact_phone: selectedCustomer.main_contact_phone || '',
        location_id: '',
        service_location: '',
        local_rep_name: '',
        local_rep_email: '',
        local_rep_phone: ''
      }));
      fetchLocationsByCustomer(customerId);
      toast.success(`Customer selected: ${selectedCustomer.name || 'Unknown'}`);
    } else {
      console.log('⚠️ [handleCustomerChange] Customer not found in customers list:', customerId);
    }
  };

  const handleLocationChange = (locationId) => {
    if (!locationId) {
      setFormData(prev => ({
        ...prev,
        location_id: '',
        service_location: ''
      }));
      return;
    }
    
    const selectedLocation = serviceLocations.find(l => l.id === locationId);
    if (selectedLocation) {
      setFormData(prev => ({
        ...prev,
        location_id: locationId,
        service_location: selectedLocation.location_name,
        service_address: selectedLocation.address || '',
        service_city: selectedLocation.city || '',
        service_state: selectedLocation.state || '',
        service_zip: selectedLocation.zip || '',
        local_rep_name: selectedLocation.local_rep_name || '',
        local_rep_email: selectedLocation.local_rep_email || '',
        local_rep_phone: selectedLocation.local_rep_phone || ''
      }));
      if (selectedLocation.local_rep_name) {
        toast.info(`Local contact loaded: ${selectedLocation.local_rep_name}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.job_name || !formData.trade_type || !formData.description_of_work || !formData.priority_level || !formData.required_service_date || !formData.assigned_to) {
      toast.error('Please fill in all required fields: Job Name, Trade Type, Description, Priority, Service Date, and Assigned Field User');
      return;
    }

    // Validate customer info
    if (!useOneOffCustomer && !formData.customer_id) {
      toast.error('Please select a customer or use one-off customer');
      return;
    }
    
    if (useOneOffCustomer && !formData.customer_name) {
      toast.error('Please enter customer name for one-off customer');
      return;
    }

    // Validate location info
    if (!useOneOffLocation && !formData.location_id) {
      toast.error('Please select a service location or use one-off location');
      return;
    }
    
    if (useOneOffLocation && !formData.service_location) {
      toast.error('Please enter service location for one-off location');
      return;
    }

    try {
      const submitData = {
        ...formData,
        // Clear IDs if using one-off
        customer_id: useOneOffCustomer ? null : formData.customer_id,
        location_id: useOneOffLocation ? null : formData.location_id
      };

      if (isEditMode) {
        await axios.put(`${API}/work-orders/${editData.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Work order updated successfully');
      } else {
        await axios.post(`${API}/work-orders`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Work order created successfully');
      }
      
      navigate('/admin/work-orders');
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update work order' : 'Failed to create work order');
      console.error(error);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Button onClick={() => navigate('/admin/work-orders')} variant="ghost" size="sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Work Orders
        </Button>
      </div>

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {isEditMode ? 'Edit Work Order' : 'Create New Work Order'}
        </h2>
        {isEditMode && editData?.tfs_work_order_number && (
          <div className="mt-2">
            <span className="text-sm text-gray-600">TFS Work Order Number: </span>
            <span className="text-2xl font-bold text-red-600">{editData.tfs_work_order_number}</span>
          </div>
        )}
      </div>

      {/* DEBUG SECTION - WORK ORDER DATA */}
      {isEditMode && editData && (
        <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded">
          <h3 className="font-bold text-lg mb-3 text-yellow-900">🔍 DEBUG WORK ORDER DATA</h3>
          <div className="grid grid-cols-2 gap-2 text-sm font-mono">
            <div><strong>Work Order ID:</strong> {editData.id}</div>
            <div><strong>WO Number:</strong> {editData.tfs_work_order_number}</div>
            <div><strong>editData.priority_level:</strong> <span className="text-red-600 font-bold">{editData.priority_level || '(EMPTY)'}</span></div>
            <div><strong>formData.priority_level:</strong> <span className="text-red-600 font-bold">{formData.priority_level || '(EMPTY)'}</span></div>
            <div><strong>editData.customer_id:</strong> <span className="text-blue-600 font-bold">{editData.customer_id || '(EMPTY)'}</span></div>
            <div><strong>formData.customer_id:</strong> <span className="text-blue-600 font-bold">{formData.customer_id || '(EMPTY)'}</span></div>
            <div><strong>editData.customer_name:</strong> {editData.customer_name || '(EMPTY)'}</div>
            <div><strong>formData.customer_name:</strong> {formData.customer_name || '(EMPTY)'}</div>
            <div><strong>editData.location_id:</strong> <span className="text-purple-600 font-bold">{editData.location_id || '(EMPTY)'}</span></div>
            <div><strong>formData.location_id:</strong> <span className="text-purple-600 font-bold">{formData.location_id || '(EMPTY)'}</span></div>
            <div><strong>editData.service_location:</strong> <span className="text-purple-600 font-bold">{editData.service_location || '(EMPTY)'}</span></div>
            <div><strong>formData.service_location:</strong> <span className="text-purple-600 font-bold">{formData.service_location || '(EMPTY)'}</span></div>
            <div><strong>editData.trade_type:</strong> <span className="text-green-600 font-bold">{editData.trade_type || '(EMPTY)'}</span></div>
            <div><strong>formData.trade_type:</strong> <span className="text-green-600 font-bold">{formData.trade_type || '(EMPTY)'}</span></div>
            <div><strong>editData.contact_person:</strong> {editData.contact_person || '(EMPTY)'}</div>
            <div><strong>formData.contact_person:</strong> {formData.contact_person || '(EMPTY)'}</div>
            <div><strong>editData.assigned_to:</strong> {editData.assigned_to ? editData.assigned_to.substring(0, 20) : '(EMPTY)'}</div>
            <div><strong>formData.assigned_to:</strong> {formData.assigned_to ? formData.assigned_to.substring(0, 20) : '(EMPTY)'}</div>
            <div className="col-span-2 mt-2 pt-2 border-t border-yellow-300">
              <strong>Customers Loaded:</strong> {customers.length} | <strong>Field Users Loaded:</strong> {fieldUsers.length} | <strong>Service Locations Loaded:</strong> {serviceLocations.length}
            </div>
            <div className="col-span-2">
              <strong>useOneOffLocation:</strong> {useOneOffLocation ? 'TRUE' : 'FALSE'}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Job Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Building2 className="h-5 w-5 mr-2" />
              Section 1: Job Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 1. Customer Selection - FIRST */}
            <div className="flex items-center space-x-2 mb-2">
              <Checkbox
                id="oneOffCustomer"
                checked={useOneOffCustomer}
                onCheckedChange={(checked) => {
                  setUseOneOffCustomer(checked);
                  if (checked) {
                    setFormData(prev => ({...prev, customer_id: '', customer_name: ''}));
                  }
                }}
              />
              <Label htmlFor="oneOffCustomer" className="text-sm cursor-pointer">
                Use One-Off Customer (not in database)
              </Label>
            </div>

            {!useOneOffCustomer ? (
              <div>
                <Label htmlFor="customer">Customer *</Label>
                {customers.length === 0 ? (
                  <div className="text-sm text-gray-500 p-3 border rounded">
                    Loading customers...
                  </div>
                ) : (
                  <Select value={formData.customer_id || ''} onValueChange={handleCustomerChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(customer => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name || customer.company_name || 'Unknown Customer'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="customer_name">Customer Name *</Label>
                  <Input
                    id="customer_name"
                    value={formData.customer_name}
                    onChange={(e) => setFormData(prev => ({...prev, customer_name: e.target.value}))}
                    placeholder="Enter customer name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="customer_email">Customer Email</Label>
                  <Input
                    id="customer_email"
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData(prev => ({...prev, customer_email: e.target.value}))}
                    placeholder="customer@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="customer_phone">Customer Phone</Label>
                  <Input
                    id="customer_phone"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData(prev => ({...prev, customer_phone: e.target.value}))}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            )}

            {/* 2. Main Customer Contact - Auto-populated */}
            {formData.main_contact_name && (
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-sm text-blue-900 mb-2">Main Customer Contact (Auto-populated)</h4>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <div className="font-medium">{formData.main_contact_name}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <div className="font-medium">{formData.main_contact_email || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <div className="font-medium">{formData.main_contact_phone || 'N/A'}</div>
                  </div>
                </div>
              </div>
            )}

            {/* 3. Job Name & Trade Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="job_name">Job Name *</Label>
                <Input
                  id="job_name"
                  value={formData.job_name}
                  onChange={(e) => setFormData(prev => ({...prev, job_name: e.target.value}))}
                  placeholder="Enter job name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="trade_type">Trade Type *</Label>
                <Select value={formData.trade_type || ''} onValueChange={(value) => setFormData(prev => ({...prev, trade_type: value}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select trade type" />
                  </SelectTrigger>
                  <SelectContent>
                    {tradeTypes.map(trade => (
                      <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 4. Description of Work */}
            <div>
              <Label htmlFor="description_of_work">Description of Work Requested *</Label>
              <Textarea
                id="description_of_work"
                value={formData.description_of_work}
                onChange={(e) => setFormData(prev => ({...prev, description_of_work: e.target.value}))}
                placeholder="Enter detailed description of work requested..."
                rows={4}
                required
              />
            </div>

            {/* 5. Work Ordered By & Reference Numbers */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="work_order_received_by">Work Ordered By</Label>
                <Input
                  id="work_order_received_by"
                  value={formData.work_order_received_by}
                  onChange={(e) => setFormData(prev => ({...prev, work_order_received_by: e.target.value}))}
                  placeholder="Received by"
                />
              </div>
              <div>
                <Label htmlFor="customer_work_order_number">Customer WO # (Optional)</Label>
                <Input
                  id="customer_work_order_number"
                  value={formData.customer_work_order_number}
                  onChange={(e) => setFormData(prev => ({...prev, customer_work_order_number: e.target.value}))}
                  placeholder="Customer WO #"
                />
              </div>
              <div>
                <Label htmlFor="customer_po_number">Customer PO # (Optional)</Label>
                <Input
                  id="customer_po_number"
                  value={formData.customer_po_number}
                  onChange={(e) => setFormData(prev => ({...prev, customer_po_number: e.target.value}))}
                  placeholder="Customer PO #"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Location Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              Section 2: Location Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Local Contact - Auto-populated from location, but editable */}
            {(formData.local_rep_name || formData.location_id) && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
                <h4 className="font-semibold text-sm text-green-900 mb-2">Local Contact (Optional - Editable)</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="local_rep_name_display" className="text-xs">Name</Label>
                    <Input
                      id="local_rep_name_display"
                      value={formData.local_rep_name}
                      onChange={(e) => setFormData(prev => ({...prev, local_rep_name: e.target.value}))}
                      placeholder="Local contact name"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="local_rep_email_display" className="text-xs">Email</Label>
                    <Input
                      id="local_rep_email_display"
                      type="email"
                      value={formData.local_rep_email}
                      onChange={(e) => setFormData(prev => ({...prev, local_rep_email: e.target.value}))}
                      placeholder="local@example.com"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="local_rep_phone_display" className="text-xs">Phone</Label>
                    <Input
                      id="local_rep_phone_display"
                      value={formData.local_rep_phone}
                      onChange={(e) => setFormData(prev => ({...prev, local_rep_phone: e.target.value}))}
                      placeholder="(555) 123-4567"
                      className="text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2 mb-2">
              <Checkbox
                id="oneOffLocation"
                checked={useOneOffLocation}
                onCheckedChange={(checked) => {
                  setUseOneOffLocation(checked);
                  if (checked) {
                    setFormData(prev => ({...prev, location_id: '', service_location: ''}));
                  }
                }}
              />
              <Label htmlFor="oneOffLocation" className="text-sm cursor-pointer">
                Use One-Off Location (not in database)
              </Label>
            </div>

            {!useOneOffLocation ? (
              <>
                <div>
                  <Label htmlFor="location">Service Location *</Label>
                  {serviceLocations.length === 0 ? (
                    <div className="text-sm text-gray-500 p-3 border rounded">
                      Loading service locations...
                    </div>
                  ) : (
                    <Select 
                      value={formData.location_id || ''} 
                      onValueChange={handleLocationChange}
                      disabled={!formData.customer_id && !useOneOffCustomer}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={!formData.customer_id && !useOneOffCustomer ? "Select customer first" : "Select service location"} />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceLocations.map(location => (
                          <SelectItem key={location.id} value={location.id}>
                            {location.location_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                {/* Display full address after selection */}
                {formData.location_id && (
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">📍 Selected Location Details</h4>
                    <div className="text-sm space-y-1">
                      <div><span className="font-medium">Location:</span> {formData.service_location || 'N/A'}</div>
                      {formData.service_address && (
                        <div><span className="font-medium">Address:</span> {formData.service_address}</div>
                      )}
                      <div>
                        <span className="font-medium">City, State, ZIP:</span> {formData.service_city || 'N/A'}{formData.service_state && `, ${formData.service_state}`}{formData.service_zip ? ` ${formData.service_zip}` : ' (No ZIP)'}
                      </div>
                      {!formData.service_zip && (
                        <div className="text-xs text-amber-600 mt-1">⚠️ ZIP code missing from location record</div>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="service_location">Service Location Name *</Label>
                  <Input
                    id="service_location"
                    value={formData.service_location}
                    onChange={(e) => setFormData(prev => ({...prev, service_location: e.target.value}))}
                    placeholder="Enter location name"
                    required
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="service_address">Address</Label>
                    <Input
                      id="service_address"
                      value={formData.service_address}
                      onChange={(e) => setFormData(prev => ({...prev, service_address: e.target.value}))}
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_city">City</Label>
                    <Input
                      id="service_city"
                      value={formData.service_city}
                      onChange={(e) => setFormData(prev => ({...prev, service_city: e.target.value}))}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_state">State</Label>
                    <Input
                      id="service_state"
                      value={formData.service_state}
                      onChange={(e) => setFormData(prev => ({...prev, service_state: e.target.value}))}
                      placeholder="State"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_zip">ZIP Code</Label>
                    <Input
                      id="service_zip"
                      value={formData.service_zip}
                      onChange={(e) => setFormData(prev => ({...prev, service_zip: e.target.value}))}
                      placeholder="ZIP"
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Job Timing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Section 3: Job Timing & Assignment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="date_received">Date Received *</Label>
                <Input
                  id="date_received"
                  type="date"
                  value={formData.date_received}
                  onChange={(e) => setFormData(prev => ({...prev, date_received: e.target.value}))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="required_service_date">Required Service Date *</Label>
                <Input
                  id="required_service_date"
                  type="date"
                  value={formData.required_service_date}
                  onChange={(e) => setFormData(prev => ({...prev, required_service_date: e.target.value}))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="priority_level">Priority Level *</Label>
                <Select 
                  value={formData.priority_level || ''} 
                  onValueChange={(value) => {
                    console.log('🎯 Priority changed to:', value);
                    setFormData(prev => ({...prev, priority_level: value}));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {priorities.map(priority => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.priority_level && (
                  <p className="text-sm text-green-600 mt-1">
                    ✓ Priority: {priorities.find(p => p.value === formData.priority_level)?.label}
                  </p>
                )}
              </div>
            </div>
            
            {/* CRITICAL: Field User Assignment */}
            <div className="pt-4 border-t">
              <Label htmlFor="assigned_to" className="text-base font-semibold">Assign to Field User *</Label>
              <p className="text-sm text-gray-600 mb-2">Select the field technician who will complete this work order</p>
              {fieldUsers.length === 0 ? (
                <div className="text-sm text-gray-500 p-3 border rounded">
                  Loading field users...
                </div>
              ) : (
                <Select 
                  value={formData.assigned_to || ''} 
                  onValueChange={(value) => {
                    console.log('👤 Assigned To changed to:', value);
                    setFormData(prev => ({...prev, assigned_to: value}));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select field user" />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldUsers.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {formData.assigned_to && fieldUsers.length > 0 && (
                <p className="text-sm text-green-600 mt-1">
                  ✓ Assigned to: {fieldUsers.find(u => u.id === formData.assigned_to)?.name || 'Unknown User'}
                </p>
              )}
              {formData.assigned_to && !fieldUsers.find(u => u.id === formData.assigned_to) && fieldUsers.length > 0 && (
                <p className="text-sm text-red-600 mt-1">
                  ⚠ Warning: Assigned user ID "{formData.assigned_to}" not found in available users
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Additional Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Section 4: Additional Instructions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="special_instructions">Additional Instructions (Optional)</Label>
              <Textarea
                id="special_instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData(prev => ({...prev, special_instructions: e.target.value}))}
                placeholder="Enter any additional instructions or notes..."
                rows={4}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="billable"
                checked={formData.billable}
                onCheckedChange={(checked) => setFormData(prev => ({...prev, billable: checked}))}
              />
              <Label htmlFor="billable" className="cursor-pointer">
                This work order is billable
              </Label>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/work-orders')}>
            Cancel
          </Button>
          <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
            <Save className="h-4 w-4 mr-2" />
            {isEditMode ? 'Update Work Order' : 'Create Work Order'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminWorkOrderForm;
